import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Project } from '../../types/domain';
import { apiClient } from '../../services/api';
import { ConfirmModal } from '../modals/ConfirmModal';
import { HedgehogSpinner } from '../brand/HedgehogSpinner';
import { IdCell } from '../grid/IdCell';
import { DataGridPagination } from '../grid/DataGridPagination';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  File,
  FileText,
  FileCode,
  FileArchive,
  Image as ImageIcon,
  Download,
  Trash2,
  Search,
  RefreshCw,
  ExternalLink,
  Copy,
  Check,
  Eye,
  AlertTriangle,
  HardDrive,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  FileSpreadsheet,
} from 'lucide-react';

export interface UploadedFileItem {
  id: string;
  organization_id: string;
  project_id: string;
  table_id: string;
  submission_id?: string;
  field_key: string;
  original_filename: string;
  storage_key: string;
  content_type: string;
  size_bytes: number;
  sha256_hash: string;
  is_spam: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  gateway_url: string;
  public_url: string;
}

interface FilesTabProps {
  table: Table;
  project: Project;
  canEdit?: boolean;
}

type FileCategory = 'all' | 'images' | 'documents' | 'archives' | 'other';

function formatBytes(bytes: number, decimals = 1): string {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function getFileCategory(contentType: string, filename: string): FileCategory {
  const ct = (contentType || '').toLowerCase();
  const ext = filename.split('.').pop()?.toLowerCase() || '';

  if (ct.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif'].includes(ext)) {
    return 'images';
  }
  if (
    ct === 'application/pdf' ||
    ct.includes('document') ||
    ct.includes('word') ||
    ct.includes('sheet') ||
    ct.includes('text/') ||
    ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'rtf', 'md'].includes(ext)
  ) {
    return 'documents';
  }
  if (
    ct.includes('zip') ||
    ct.includes('tar') ||
    ct.includes('gzip') ||
    ct.includes('compressed') ||
    ['zip', 'tar', 'gz', '7z', 'rar'].includes(ext)
  ) {
    return 'archives';
  }
  return 'other';
}

function renderFileIcon(contentType: string, filename: string) {
  const category = getFileCategory(contentType, filename);
  const ext = filename.split('.').pop()?.toLowerCase() || '';

  if (category === 'images') {
    return <ImageIcon size={18} className="text-emerald-400 shrink-0" />;
  }
  if (category === 'archives') {
    return <FileArchive size={18} className="text-amber-400 shrink-0" />;
  }
  if (ext === 'csv' || ext === 'xlsx' || ext === 'xls') {
    return <FileSpreadsheet size={18} className="text-teal-400 shrink-0" />;
  }
  if (['json', 'xml', 'yaml', 'yml', 'js', 'ts', 'html', 'css'].includes(ext)) {
    return <FileCode size={18} className="text-sky-400 shrink-0" />;
  }
  if (category === 'documents') {
    return <FileText size={18} className="text-blue-400 shrink-0" />;
  }
  return <File size={18} className="text-zinc-400 shrink-0" />;
}

export const FilesTab: React.FC<FilesTabProps> = ({ table, project, canEdit = false }) => {
  const [files, setFiles] = useState<UploadedFileItem[]>([]);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(50);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<FileCategory>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Deletion state
  const [fileToDelete, setFileToDelete] = useState<UploadedFileItem | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Preview modal state
  const [previewFile, setPreviewFile] = useState<UploadedFileItem | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewFile) {
        setPreviewFile(null);
      }
    };
    if (previewFile) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewFile]);

  const fetchFiles = useCallback(
    async (isSilent = false) => {
      if (!table?.id || !project?.id) return;
      if (!isSilent) setIsLoading(true);
      else setIsRefreshing(true);

      try {
        const resp = await apiClient<{
          data: UploadedFileItem[];
          total: number;
          page: number;
          limit: number;
        }>(`/projects/${project.id}/tables/${table.id}/files`, {
          params: {
            page,
            limit,
            search: searchQuery.trim() || undefined,
          },
        });

        setFiles(resp.data || []);
        setTotalFiles(resp.total || 0);
      } catch (err) {
        console.error('Failed to load table files:', err);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [project?.id, table?.id, page, limit, searchQuery]
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    fetchFiles(false);
  }, [fetchFiles]);

  const handleDelete = async () => {
    if (!fileToDelete || !project?.id || !table?.id) return;
    setIsDeleting(true);
    try {
      await apiClient(`/projects/${project.id}/tables/${table.id}/files/${fileToDelete.id}`, {
        method: 'DELETE',
      });
      setFileToDelete(null);
      await fetchFiles(true);
    } catch (err: any) {
      alert(`Failed to delete file: ${err.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyLink = (file: UploadedFileItem) => {
    const url = file.gateway_url || file.public_url;
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(file.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filter client-side by category pill if needed
  const filteredFiles = useMemo(() => {
    if (activeCategory === 'all') return files;
    return files.filter(
      (f) => getFileCategory(f.content_type, f.original_filename) === activeCategory
    );
  }, [files, activeCategory]);

  // Compute stats on current page
  const pageStorageBytes = useMemo(() => {
    return files.reduce((acc, f) => acc + (f.size_bytes || 0), 0);
  }, [files]);

  const totalPages = Math.ceil(totalFiles / limit) || 1;

  const isPreviewable = (f: UploadedFileItem) => {
    const ct = (f.content_type || '').toLowerCase();
    return (
      (ct.startsWith('image/') && ct !== 'image/svg+xml') ||
      ct === 'application/pdf' ||
      ct === 'text/plain'
    );
  };

  return (
    <div className="flex flex-col w-full bg-cf-bg text-cf-textPrimary space-y-4">
      {/* Top Banner & Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-cf-surface border border-cf-borderSubtle rounded-lg p-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold tracking-wider text-cf-textMuted uppercase">
              Total Table Files
            </div>
            <div className="text-2xl font-bold tracking-tight text-cf-textPrimary mt-1">
              {totalFiles.toLocaleString()}
            </div>
          </div>
          <div className="w-10 h-10 rounded-md bg-cf-surfaceHover flex items-center justify-center text-cf-textSecondary">
            <HardDrive size={20} />
          </div>
        </div>

        <div className="bg-cf-surface border border-cf-borderSubtle rounded-lg p-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold tracking-wider text-cf-textMuted uppercase">
              Files on This Page
            </div>
            <div className="text-2xl font-bold tracking-tight text-cf-textPrimary mt-1">
              {files.length}
            </div>
          </div>
          <div className="w-10 h-10 rounded-md bg-cf-surfaceHover flex items-center justify-center text-cf-textSecondary">
            <Filter size={20} />
          </div>
        </div>

        <div className="bg-cf-surface border border-cf-borderSubtle rounded-lg p-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold tracking-wider text-cf-textMuted uppercase">
              Storage on This Page
            </div>
            <div className="text-2xl font-bold tracking-tight text-cf-textPrimary mt-1">
              {formatBytes(pageStorageBytes)}
            </div>
          </div>
          <div className="w-10 h-10 rounded-md bg-cf-surfaceHover flex items-center justify-center text-cf-textSecondary">
            <FileText size={20} />
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-cf-surface border border-cf-borderSubtle rounded-lg p-3">
        {/* Search input */}
        <div className="flex-1 max-w-md">
          <Input
            type="text"
            size="xs"
            leftIcon={<Search size={14} />}
            placeholder="Search by filename or column key..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          {(
            [
              { id: 'all', label: 'All Files' },
              { id: 'images', label: 'Images' },
              { id: 'documents', label: 'Documents' },
              { id: 'archives', label: 'Archives' },
              { id: 'other', label: 'Other' },
            ] as const
          ).map((cat) => (
            <Button
              key={cat.id}
              type="button"
              variant={activeCategory === cat.id ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setActiveCategory(cat.id)}
              className={activeCategory === cat.id ? 'text-cf-textPrimary border border-cf-border shadow-xs' : ''}
            >
              {cat.label}
            </Button>
          ))}

          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => fetchFiles(true)}
            disabled={isRefreshing}
            title="Refresh files"
            aria-label="Refresh files"
            className="ml-1"
          >
            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Files Data Table */}
      <div className="bg-cf-surface border border-cf-borderSubtle rounded-lg overflow-hidden flex flex-col flex-1 min-h-[360px]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center flex-1 py-16">
            <HedgehogSpinner size="md" label="LOADING FILES..." />
          </div>
        ) : files.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 py-16 text-center px-4">
            <File size={36} className="text-cf-textMuted mb-3 opacity-60" />
            <h3 className="text-sm font-semibold text-cf-textPrimary">No files found</h3>
            <p className="text-xs text-cf-textSecondary mt-1 max-w-sm">
              {searchQuery || activeCategory !== 'all'
                ? 'No uploaded files match the selected filter criteria.'
                : 'Form submissions with file upload fields (multipart/form-data) will be organized and listed here automatically.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-cf-borderSubtle dark:border-[#2e323c] bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader text-cf-textMuted font-medium uppercase text-xs tracking-wider">
                  <th className="py-2.5 px-4 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">File Name</th>
                  <th className="py-2.5 px-3 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Column Key</th>
                  <th className="py-2.5 px-3 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">MIME Type</th>
                  <th className="py-2.5 px-3 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Size</th>
                  <th className="py-2.5 px-3 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Submission / Row</th>
                  <th className="py-2.5 px-3 bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Uploaded</th>
                  <th className="py-2.5 px-4 text-right bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cf-borderSubtle/60">
                {files.map((file) => {
                  const previewable = isPreviewable(file);
                  const isCopied = copiedId === file.id;

                  return (
                    <tr
                      key={file.id}
                      className="bg-cf-surface hover:bg-cf-surfaceHover/60 transition-colors group"
                    >
                      {/* Name & Icon */}
                      <td className="py-2.5 px-4 font-medium text-cf-textPrimary max-w-[280px]">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {renderFileIcon(file.content_type, file.original_filename)}
                          <span
                            className="truncate select-all text-cf-textPrimary hover:underline cursor-pointer"
                            title={file.original_filename}
                            onClick={() => {
                              if (previewable) setPreviewFile(file);
                              else window.open(file.gateway_url, '_blank');
                            }}
                          >
                            {file.original_filename}
                          </span>
                          {file.is_spam && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-xs font-sans shrink-0 border border-amber-500/20">
                              SPAM
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Column Key */}
                      <td className="py-2.5 px-3 text-cf-textSecondary font-sans text-xs">
                        <span className="px-1.5 py-0.5 bg-cf-bg rounded border border-cf-borderSubtle">
                          {file.field_key || 'file'}
                        </span>
                      </td>

                      {/* MIME Type */}
                      <td className="py-2.5 px-3 text-cf-textMuted font-sans text-xs truncate max-w-[140px]">
                        {file.content_type || 'application/octet-stream'}
                      </td>

                      {/* Size */}
                      <td className="py-2.5 px-3 text-cf-textSecondary font-sans text-xs tabular-nums whitespace-nowrap">
                        {formatBytes(file.size_bytes)}
                      </td>

                      {/* Submission ID */}
                      <td className="py-2.5 px-3 max-w-[140px]">
                        {file.submission_id ? (
                          <IdCell id={file.submission_id} />
                        ) : (
                          <span className="text-zinc-600 font-sans text-xs">—</span>
                        )}
                      </td>

                      {/* Uploaded At */}
                      <td className="py-2.5 px-3 text-cf-textMuted whitespace-nowrap text-xs font-sans">
                        {formatDate(file.created_at)}
                      </td>

                      {/* Actions */}
                      <td className="py-2.5 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1 opacity-90 group-hover:opacity-100 transition-opacity">
                          {/* Preview button (for images/pdf/text) */}
                          {previewable && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              onClick={() => setPreviewFile(file)}
                              title="Preview file"
                              aria-label="Preview file"
                            >
                              <Eye size={14} />
                            </Button>
                          )}

                          {/* Download directly */}
                          <Button
                            href={file.gateway_url}
                            variant="ghost"
                            size="icon-xs"
                            download={file.original_filename}
                            title="Download attachment"
                            aria-label="Download attachment"
                          >
                            <Download size={14} />
                          </Button>

                          {/* Copy Gateway Link */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            onClick={() => handleCopyLink(file)}
                            title="Copy signed access URL"
                            aria-label="Copy signed access URL"
                          >
                            {isCopied ? (
                              <Check size={14} className="text-emerald-400" />
                            ) : (
                              <Copy size={14} />
                            )}
                          </Button>

                          {/* Open link externally */}
                          <Button
                            href={file.gateway_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="ghost"
                            size="icon-xs"
                            title="Open in new window"
                            aria-label="Open in new window"
                          >
                            <ExternalLink size={14} />
                          </Button>

                          {/* Delete */}
                          {canEdit && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              onClick={() => setFileToDelete(file)}
                              title="Permanently delete file"
                              aria-label="Permanently delete file"
                              className="hover:text-rose-400 ml-1"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalFiles > limit && (
          <DataGridPagination
            page={page}
            pageSize={limit}
            totalRows={totalFiles}
            unitLabel="files"
            showPageSize={false}
            onPageChange={(p) => setPage(p)}
          />
        )}
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setPreviewFile(null);
            }
          }}
        >
          <div
            className="bg-cf-surface border border-cf-borderSubtle rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-cf-borderSubtle bg-cf-bg/50">
              <div className="flex items-center gap-2 min-w-0 pr-4">
                {renderFileIcon(previewFile.content_type, previewFile.original_filename)}
                <span className="text-xs font-semibold text-cf-textPrimary truncate">
                  {previewFile.original_filename}
                </span>
                <span className="text-xs text-cf-textMuted font-sans">
                  ({formatBytes(previewFile.size_bytes)})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  href={previewFile.gateway_url}
                  download={previewFile.original_filename}
                  variant="secondary"
                  size="xs"
                  leftIcon={<Download size={13} />}
                >
                  Download
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setPreviewFile(null)}
                  aria-label="Close preview"
                >
                  <X size={16} />
                </Button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-black/40 min-h-[300px]">
              {previewFile.content_type?.startsWith('image/') ? (
                <img
                  src={
                    previewFile.gateway_url.includes('?')
                      ? `${previewFile.gateway_url}&preview=1`
                      : `${previewFile.gateway_url}?preview=1`
                  }
                  alt={previewFile.original_filename}
                  className="max-h-[70vh] max-w-full object-contain rounded shadow"
                />
              ) : previewFile.content_type === 'application/pdf' ? (
                <iframe
                  src={
                    previewFile.gateway_url.includes('?')
                      ? `${previewFile.gateway_url}&preview=1`
                      : `${previewFile.gateway_url}?preview=1`
                  }
                  title={previewFile.original_filename}
                  className="w-full h-[70vh] border-0 rounded bg-white"
                />
              ) : (
                <div className="text-center py-12 text-cf-textSecondary">
                  <FileText size={48} className="mx-auto mb-3 text-cf-textMuted" />
                  <p className="text-xs">Inline preview unavailable for this file format.</p>
                  <a
                    href={previewFile.gateway_url}
                    download={previewFile.original_filename}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-cf-orange text-cf-bg rounded font-medium text-xs hover:bg-cf-orangeHover transition-colors"
                  >
                    <Download size={14} />
                    <span>Download to View</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {fileToDelete && (
        <ConfirmModal
          isOpen={true}
          title="Delete Uploaded File"
          message={`Are you sure you want to permanently delete "${fileToDelete.original_filename}"? This will purge the file binary from object storage and cannot be undone.`}
          confirmLabel="Delete File"
          isDestructive={true}
          isLoading={isDeleting}
          onConfirm={handleDelete}
          onClose={() => setFileToDelete(null)}
        />
      )}
    </div>
  );
};
